import { Accessor, createMemo, createSignal, For, onCleanup, onMount } from 'solid-js';
import clsx from 'clsx';

export type MovingCirclesProps = {
    movingCirclesColor: Accessor<string>;
    movingCirclesPosition: Accessor<string>;
    isClientPage: Accessor<boolean>;
    hash: string;
};

const SCROLL_DELAY_PX = 250;

export const MovingCircles = ({ movingCirclesColor, movingCirclesPosition, hash, isClientPage }: MovingCirclesProps) => {
    const [scrollY, setScrollY] = createSignal(0);
    const effectiveScroll = () => Math.max(0, scrollY() - SCROLL_DELAY_PX);

    const color = () => movingCirclesColor();
    const layout = () => movingCirclesPosition();

    const getRandomAngle = () => `${Math.floor(Math.random() * 360)}deg`;

    const randomVh = (min: number, max: number) => `${Math.floor(Math.random() * (max - min + 1)) + min}vh`;

    const circles = createMemo(() => {
        const layoutType = layout();
        return (layoutType === 'both-sides'
            ? [
                { delay: '1s', size: 12, topVh: randomVh(20, 50), side: '-right-20', scrollSpeed: 0.7 },
                { delay: '4s', size: 8, topVh: randomVh(70, 90), side: '-left-20', scrollSpeed: 0.3 }
            ]
            : [
                { delay: '0s', size: 16, topVh: randomVh(25, 40), side: '-right-16', scrollSpeed: 0.4 },
                { delay: '2s', size: 4, topVh: randomVh(16, 22), side: 'right-60', scrollSpeed: -0.5 }
            ]
        ).map((circle) => ({
            ...circle,
            angle: getRandomAngle()
        }));
    });

    const onScroll = () => setScrollY(window.scrollY);

    onMount(() => {
        window.addEventListener('scroll', onScroll);
    });

    onCleanup(() => {
        window.removeEventListener('scroll', onScroll);
    });

    return (
        <div class='hidden pointer-events-none desktop:block absolute inset-0 overflow-hidden z-10'
            classList={{ 'laptop:block' : isClientPage() }}
        >
            <For each={circles()}>
                {({ delay, size, topVh, side, scrollSpeed, angle }, index) => {
                    const id = `${hash}${index().toString()}`;
                    const baseGradient = color() === 'pink-yellow'
                        ? `linear-gradient(${angle}, #CC0099CC 2.2%, #F0EE16CC 88.58%)`
                        : `linear-gradient(${angle}, #FF5050 2.2%, #A200FF 88.58%)`;
        

                    const style = () => ({
                        width: `${size}rem`,
                        height: `${size}rem`,
                        background: baseGradient,
                        'background-size': '200% 200%',
                        'animation-delay': delay,
                    });

                    return (
                        <div
                            id={id}
                            class={clsx('absolute', side)}
                            style={{
                                top: `calc(${topVh} - ${size / 2}rem)`,
                                transform: `translateY(${effectiveScroll() * scrollSpeed}px)`,
                                transition: 'transform 0.8s ease-out',
                            }}
                        >
                            <div
                                class='rounded-full animate-circle-gradient'
                                style={style()}
                            />
                        </div>
                    );
                }}
            </For>
        </div>
    );
};

MovingCircles.parseProps = (atts: MovingCirclesProps) => {
    return {
        movingCirclesColor: atts.movingCirclesColor,
        movingCirclesPosition: atts.movingCirclesPosition,
        hash: atts.hash,
    };
};
